import { BoothFeedbackCategory } from '@cibli/shared';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@cibli/shared';
import { Speaker } from '@micdrop/web';
import { ArrowLeft, Languages } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { PlatformBridge } from '@/bridge/bridge';
import PhoneInput from '@/features/common/components/PhoneInput';

import type { FeedbackAudioKey } from './hooks/useFeedbackAssistant';

import { AssistantLogo } from './components/AssistantLogo';
import { CategoryGrid } from './components/CategoryGrid';
import { DeleteClipDialog } from './components/DeleteClipDialog';
import { FeedbackConfirmation } from './components/FeedbackConfirmation';
import { OfflineNotice } from './components/OfflineNotice';
import { VoiceClipList, type VoiceClip } from './components/VoiceClipList';
import { VoiceRecorder } from './components/VoiceRecorder';
import { CATEGORY_I18N_MAP } from './constants';
import { useFeedbackContext } from './feedback.provider';
import { useFeedbackCategories } from './hooks/useFeedbackCategories';
import { useFeedbackLocale, type FeedbackLocaleCode } from './hooks/useFeedbackLocale';
import { useFeedbackQueue } from './hooks/useFeedbackQueue';
import { useSubmitFeedback } from './hooks/useSubmitFeedback';

type FeedbackStep = 'category' | 'detail' | 'email' | 'confirmation';

interface FeedbackSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boothId?: string;
  sessionId?: string;
  flowStep?: string;
  lastMessage?: string;
}

export function FeedbackSheet({
  open,
  onOpenChange,
  boothId,
  sessionId,
  flowStep,
  lastMessage,
}: FeedbackSheetProps) {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<FeedbackStep>('category');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [clips, setClips] = useState<VoiceClip[]>([]);
  const [comment, setComment] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [phoneComplete, setPhoneComplete] = useState(false);

  const handlePhoneChange = useCallback((phone: string, isComplete: boolean) => {
    setUserPhone(phone);
    setPhoneComplete(isComplete);
  }, []);

  const { categories } = useFeedbackCategories();
  const { submit: submitFeedback, progress } = useSubmitFeedback();
  const { feedbackLocale, isRTL, setOverride, availableLocales } = useFeedbackLocale();
  const { playAssistant, stopAssistant } = useFeedbackContext();
  const { queueCount } = useFeedbackQueue();

  const selectedCategoryI18nKey = selectedCategory
    ? CATEGORY_I18N_MAP[selectedCategory]
    : undefined;
  const selectedCategoryLabel = selectedCategoryI18nKey ? t(selectedCategoryI18nKey) : '';

  const canSubmit = selectedCategory && (clips.length > 0 || comment.length >= 10);

  // Play ready_to_send when form becomes valid (spec §13)
  const wasValidRef = useRef(false);
  useEffect(() => {
    if (canSubmit && !wasValidRef.current) {
      wasValidRef.current = true;
      playAssistant('ready_to_send');
    }
    if (!canSubmit) wasValidRef.current = false;
  }, [canSubmit, playAssistant]);

  const detailSequenceRef = useRef(false);

  const handleCategorySelect = async (category: string) => {
    setSelectedCategory(category);
    setStep('detail');
    detailSequenceRef.current = true;
    try {
      // Play category_confirm, then record_rules — sequential
      await playAssistant('category_confirm', category as BoothFeedbackCategory);
      if (detailSequenceRef.current) {
        await playAssistant('record_rules');
      }
    } catch {
      // Audio failure is non-critical — continue without audio
    }
  };

  const handleClipRecorded = useCallback(
    (blob: Blob, durationMs: number) => {
      setClips(prev => [...prev, { id: `clip-${Date.now()}-${prev.length}`, blob, durationMs }]);
      playAssistant('clip_saved');
    },
    [playAssistant],
  );

  const handleDeleteClip = useCallback((clipId: string) => {
    setDeleteTargetId(clipId);
  }, []);

  const confirmDelete = useCallback(() => {
    if (deleteTargetId) {
      setClips(prev => prev.filter(c => c.id !== deleteTargetId));
      setDeleteTargetId(null);
    }
  }, [deleteTargetId]);

  const handleSubmit = async () => {
    setStep('email');
  };

  const handleEmailSubmit = async (email?: string, phone?: string) => {
    if (!selectedCategory) return;

    try {
      await submitFeedback({
        boothId,
        sessionId,
        category: selectedCategory,
        comment: comment || undefined,
        audioClips: clips,
        locale: feedbackLocale,
        source: PlatformBridge.isCapacitor ? 'ANDROID_APP' : 'WEB',
        flowStep: flowStep || window.location.pathname,
        lastMessage: lastMessage || undefined,
        userEmail: email || undefined,
        userPhone: phone || undefined,
      });
      setStep('confirmation');
      playAssistant('sent_online');
    } catch {
      if (!navigator.onLine) {
        playAssistant('queued_offline');
        setStep('confirmation');
      }
    }
  };

  // Cancel the pending category_confirm → record_rules sequence when the user
  // taps "Parler", so instructions never replay over the armed/recording state
  const handleRecorderAudio = useCallback(
    (key: FeedbackAudioKey) => {
      if (key === 'recording_active') detailSequenceRef.current = false;
      return playAssistant(key);
    },
    [playAssistant],
  );

  const handleBack = async () => {
    if (step === 'detail') {
      detailSequenceRef.current = false;
      if (Speaker.isPlaying) Speaker.stopAudio();
      setStep('category');
      setSelectedCategory(null);
      setClips([]);
      setComment('');
      try {
        await playAssistant('categories_intro');
      } catch {
        // Audio failure is non-critical
      }
    }
  };

  const handleClose = () => {
    // Cancel any pending audio sequence immediately
    detailSequenceRef.current = false;
    // Stop all playing audio
    if (Speaker.isPlaying) Speaker.stopAudio();
    setStep('category');
    setSelectedCategory(null);
    setClips([]);
    setComment('');
    setShowLangPicker(false);
    onOpenChange(false);
  };

  const handleLangChange = async (code: string) => {
    await i18n.changeLanguage(code);

    setOverride(code as FeedbackLocaleCode);
    setShowLangPicker(false);

    const stepAudioKeyMap: Record<string, string> = {
      category: 'categories_intro',
      detail: 'record_rules',
      confirmation: 'sent_online',
    };
    const audioKey = stepAudioKeyMap[step] ?? 'categories_intro';
    if (Speaker.isPlaying) Speaker.stopAudio();
    try {
      await playAssistant(audioKey as 'categories_intro' | 'record_rules' | 'sent_online');
    } catch {
      // Audio failure is non-critical
    }
  };

  const currentLangName = useMemo(() => {
    return availableLocales.find(l => l.code === feedbackLocale)?.name ?? feedbackLocale;
  }, [feedbackLocale, availableLocales]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.trim());
  const contactValid = (emailValid || phoneComplete) && (userEmail.trim() === '' || emailValid);
  const showContactError = !contactValid && (userEmail.trim() !== '' || userPhone.trim() !== '');

  return (
    <Dialog
      open={open}
      onOpenChange={newOpen => {
        if (!newOpen) handleClose();
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        dir={isRTL ? 'rtl' : 'ltr'}
        onPointerDownCapture={stopAssistant}
      >
        {/* Screen reader description */}
        <p className="sr-only">
          {step === 'category'
            ? t('Feedback.sheet.subtitle')
            : step === 'detail'
              ? t('Feedback.detail.instruction')
              : t('Feedback.confirmation.message')}
        </p>

        {step === 'category' && (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>{t('Feedback.sheet.title')}</DialogTitle>
                <div className="flex items-center gap-2">
                  <AssistantLogo />
                  <button
                    type="button"
                    onClick={() => setShowLangPicker(!showLangPicker)}
                    className="flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
                    aria-label={t('Feedback.lang.change')}
                  >
                    <Languages className="h-4 w-4" />
                    <span>{currentLangName}</span>
                  </button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{t('Feedback.sheet.subtitle')}</p>
            </DialogHeader>

            {showLangPicker ? (
              <div className="flex flex-col gap-1">
                {availableLocales.map(lang => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => handleLangChange(lang.code)}
                    className={`rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                      lang.code === feedbackLocale
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted'
                    }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            ) : (
              <CategoryGrid
                categories={categories}
                selected={null}
                onSelect={handleCategorySelect}
              />
            )}
          </>
        )}

        {step === 'detail' && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
                  aria-label={t('Feedback.detail.back')}
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <DialogTitle>{selectedCategoryLabel}</DialogTitle>
              </div>
            </DialogHeader>

            <p className="text-sm text-muted-foreground">{t('Feedback.detail.instruction')}</p>

            <VoiceRecorder onClipRecorded={handleClipRecorded} playAudio={handleRecorderAudio} />

            {clips.length > 0 && <VoiceClipList clips={clips} onDelete={handleDeleteClip} />}

            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={t('Feedback.detail.textPlaceholder')}
              rows={3}
              className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <p className="text-xs text-muted-foreground">{t('Feedback.detail.autoContext')}</p>

            {queueCount > 0 && <OfflineNotice queueCount={queueCount} />}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || progress === 'uploading'}
              className="w-full rounded-lg bg-primary py-3 text-primary-foreground font-medium transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {progress === 'uploading' ? t('Feedback.detail.sending') : t('Feedback.detail.send')}
            </button>
          </>
        )}

        {step === 'email' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('Feedback.email.title')}</DialogTitle>
              <p className="text-sm text-muted-foreground">{t('Feedback.email.subtitle')}</p>
            </DialogHeader>

            <div className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                <Trans
                  i18nKey="Feedback.email.description"
                  components={[<strong className="font-semibold text-foreground" />]}
                />
              </p>

              <div className="space-y-3">
                <div>
                  <label htmlFor="feedback-email-input" className="sr-only">
                    {t('Feedback.email.placeholder')}
                  </label>
                  <input
                    id="feedback-email-input"
                    type="email"
                    value={userEmail}
                    onChange={e => setUserEmail(e.target.value)}
                    placeholder={t('Feedback.email.placeholder')}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <PhoneInput value={userPhone} onChange={handlePhoneChange} compact />
              </div>

              <button
                type="button"
                onClick={() =>
                  handleEmailSubmit(
                    emailValid ? userEmail.trim() : undefined,
                    phoneComplete ? userPhone : undefined,
                  )
                }
                disabled={!contactValid || progress === 'uploading'}
                className="w-full rounded-lg bg-primary py-3 text-primary-foreground font-medium transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {progress === 'uploading' ? t('Feedback.detail.sending') : t('Feedback.email.send')}
              </button>

              {showContactError && (
                <p role="alert" className="text-sm text-destructive">
                  {t('Feedback.email.requiredError')}
                </p>
              )}
            </div>
          </>
        )}

        {step === 'confirmation' && <FeedbackConfirmation onClose={handleClose} />}

        <DeleteClipDialog
          open={deleteTargetId !== null}
          clipIndex={clips.findIndex(c => c.id === deleteTargetId)}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTargetId(null)}
          playAudio={playAssistant}
        />
      </DialogContent>
    </Dialog>
  );
}
